import type {
  GridLesson,
  GridStudent,
  GridSubscription,
  GridTeacherVacation,
  GridVacation,
} from '../store/slices/journalSlice';

/** One grid column: a real lesson, a projected future date, a collapsed teacher vacation, or the group end marker */
export type JournalColumn =
  | { kind: 'lesson'; date: string; lesson: GridLesson }
  | { kind: 'ghost'; date: string }
  | { kind: 'teacherVacation'; date: string; vacation: GridTeacherVacation }
  | { kind: 'groupEnd'; date: string };

export type JournalCellState =
  | 'out'       // not a member of the group on this date
  | 'canceled'  // column-wide: the lesson is canceled
  | 'ghost'     // projected future date, no lesson row yet
  | 'frozen'    // freeze vacation covers the date
  | 'ill'       // illness vacation covers the date
  | 'vacation'  // regular vacation covers the date
  | 'absent'    // absence row exists for (lesson, student)
  | 'attended'; // default: member, no absence, no covering vacation

export interface JournalCellResolution {
  state: JournalCellState;
  /** The vacation that produced frozen/ill/vacation, when applicable */
  vacation?: GridVacation;
}

/** Is `date` inside any membership window? Windows are [from, to) with null = unbounded. */
export function isDateInMemberships(
  date: string,
  windows: { from: string | null; to: string | null }[]
): boolean {
  return windows.some(
    (w) => (w.from === null || w.from <= date) && (w.to === null || date < w.to)
  );
}

function findCoveringVacation(student: GridStudent, date: string): GridVacation | undefined {
  return (student.vacations || []).find((v) => v.dateFrom <= date && date <= v.dateTo);
}

/**
 * Resolve the state of one cell (student × column) with fixed precedence:
 * out → canceled → frozen → ill → vacation → ghost → absent → attended.
 * Vacation states rank above ghost so planned sick leaves / pauses are
 * visible on future dates (like the teacher's Excel "пауза до …" marks).
 */
export function resolveCellState(student: GridStudent, column: JournalColumn): JournalCellResolution {
  if (!isDateInMemberships(column.date, student.memberships)) {
    return { state: 'out' };
  }

  if (column.kind === 'lesson' && column.lesson.canceled) {
    return { state: 'canceled' };
  }

  const vacation = findCoveringVacation(student, column.date);
  if (vacation?.vacationType?.isFreeze) return { state: 'frozen', vacation };
  if (vacation?.vacationType?.isIllness) return { state: 'ill', vacation };
  if (vacation) return { state: 'vacation', vacation };

  if (column.kind === 'ghost') {
    return { state: 'ghost' };
  }

  if (column.kind === 'lesson' && column.lesson.absentStudentIds.includes(student.id)) {
    return { state: 'absent' };
  }

  return { state: 'attended' };
}

/** The subscription whose first covered lesson is this date (start marker), if any. */
export function findSubscriptionStart(student: GridStudent, date: string): GridSubscription | undefined {
  return (student.subscriptions || []).find((s) => !s.isFreeze && s.startDate === date);
}

/**
 * Alternating tint index for the non-freeze package covering this date, or null.
 * Adjacent packages get different tints so boundaries are visible at a glance.
 */
export function subscriptionTintIndex(student: GridStudent, date: string): 0 | 1 | null {
  const packages = (student.subscriptions || []).filter((s) => !s.isFreeze);
  for (let i = 0; i < packages.length; i++) {
    const sub = packages[i];
    const end = sub.effectiveEndDate;
    if (sub.startDate <= date && (end === null || date <= end)) {
      return (i % 2) as 0 | 1;
    }
  }
  return null;
}

/**
 * Merge lessons, ghost dates and teacher vacations into a sorted column list.
 * When the group has an end date inside the visible range, a `groupEnd` marker
 * column is appended at that date. The sort is stable, so when the end date
 * falls on a lesson/ghost date the marker lands right AFTER that column; on a
 * date without lessons it simply takes its chronological place.
 */
export function buildColumns(
  lessons: GridLesson[],
  ghostDates: string[],
  teacherVacations: GridTeacherVacation[],
  groupEndDate: string | null = null
): JournalColumn[] {
  const columns: JournalColumn[] = [
    ...lessons.map((lesson): JournalColumn => ({ kind: 'lesson', date: lesson.date, lesson })),
    ...ghostDates.map((date): JournalColumn => ({ kind: 'ghost', date })),
    ...teacherVacations.map((vacation): JournalColumn => ({
      kind: 'teacherVacation',
      date: vacation.dateFrom,
      vacation,
    })),
  ];
  if (groupEndDate) {
    columns.push({ kind: 'groupEnd', date: groupEndDate });
  }
  return columns.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}
